import type { ScenarioSpec } from "./types";
import { officeUnderAttackScenario as OUA } from "./office-under-attack.scenario";

/**
 * Remote work and BYOD briefing. The film plays on the reception terminal.
 * The practice is the full Max remote-work course, stopped before its quiz.
 * The quiz for this briefing is only the terminal's own finish step.
 */
const VIDEO = "/cdn/sim/awareness-videos/remote-work-byod/remote-work-byod.mp4";
const POSTER = "/cdn/sim/awareness-videos/remote-work-byod/poster.jpg";
const PRACTICE = "/cdn/sim/awareness-videos/remote-work-byod/practice.jpg";

const PRACTICE_HREF =
  "/?scenario=remote-work-max&beat=b_intro&return=remote-work-byod&stop=b_quiz,b_debrief";

export const remoteWorkByodScenario: ScenarioSpec = {
  version: 1,
  id: "remote-work-byod",
  title: "Remote work & BYOD",
  summary: {
    goal: "Learn the rules for working away from the office and using your own device, and practise them through a full remote-work day.",
    youDo: "Watch the film on the reception terminal, follow Max through a day of travel and meetings if you want to practise, then answer five quiz questions.",
    duration: "Core 5-8 min, Max's day adds 15-20 min",
    tags: ["remote_work", "personal_devices", "public_wifi", "deepfakes", "incident_reporting"],
    audience: ["remote_workers", "travellers"],
    needs: ["sound"],
  },
  topic: "Working away from the office, and using your own device, still follows the company rules",
  language: "en",
  passThreshold: 60,
  allowFreeExplore: false,
  voiceAttackers: OUA.voiceAttackers,
  scene: OUA.scene,

  devices: [
    {
      id: "rw_terminal",
      kind: "breachbrief",
      config: {
        noticeTag: "REMOTE WORK",
        noticeKicker: "SECURITY AWARENESS",
        title: "Remote work & BYOD",
        offLabel: "Switch on the welcome terminal",
        voiceId: "onyx",
        progressKey: "remote-work-byod",
        idleImage: PRACTICE,
        videoFirst: true,
        lines: [
          "Working away from the office does not change what you are allowed to do with company information.",
          "Watch the film, then follow Max through a day of remote work. You come back here before the quiz.",
        ],
        hubIntro: "Watch the film, then practise Max's day. You return here when it ends.",
        hubVideo: {
          title: "Remote work & BYOD",
          blurb: "Approved devices, approved connections, and what to do when something is lost.",
          src: VIDEO,
          poster: POSTER,
        },
        exercisesKicker: "1 interactive exercise",
        exercisesLabel: "Practise",
        exercises: [
          {
            id: "rw-max",
            label: "Max's day away from the office",
            blurb: "Pack the laptop, then face the networks, calls, chargers, and devices of a remote day.",
            key: "practice",
            completionBeatId: "rw-max-done",
            href: PRACTICE_HREF,
            icon: "chapters",
            imageUrl: PRACTICE,
            actionLabel: "Practise",
            doneLabel: "Done",
          },
        ],
        cards: [],
        finishEarlyHint: "You can take the quiz now, or watch the film and follow Max's day first.",
      },
    },
  ],

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "Remote work & BYOD",
      kicker: "Reception",
      guide: { title: "Start", body: "You start at an unattended reception. The welcome terminal plays a short film about working away from the office and on your own device, then lets you practise a full remote-work day with Max before you come back for the quiz." },
    },
    { id: "b_cam_terminal", type: "camera", waypointId: "wp_terminal_close", walkSfx: true },
    {
      id: "b_hub",
      type: "device",
      deviceId: "rw_terminal",
      guide: {
        title: "Reception terminal",
        body: "Watch the film, then follow Max's day. You come back here for the quiz.",
      },
      completeOn: { action: "continue" },
    },
    {
      id: "b_quiz",
      type: "quiz",
      title: "Check what you would do",
      quiz: [
        {
          id: "q_call",
          title: "Question 1",
          prompt: "You need to take a confidential call in public. What is safest?",
          options: [
            { id: "a", label: "Put the call on speaker so you can take notes.", icon: "x" },
            { id: "b", label: "Move somewhere private, or keep it general with a headset.", correct: true, icon: "check" },
            { id: "c", label: "Confirm names and numbers quickly so the call is short.", icon: "alert" },
          ],
          rationale: "Public spaces leak audio. Postpone the sensitive parts, or stay high-level with a headset.",
        },
        {
          id: "q_lost",
          title: "Question 2",
          prompt: "You lose your work laptop. What do you do first?",
          options: [
            { id: "a", label: "Wait a day in case someone returns it.", icon: "x" },
            { id: "b", label: "Report it immediately to IT or security.", correct: true, icon: "check" },
            { id: "c", label: "Post on social media asking if anyone has seen it.", icon: "alert" },
          ],
          rationale: "Reporting early is what lets the company lock the device and close the sessions. Waiting, or a public post, makes it worse.",
        },
        {
          id: "q_wifi",
          title: "Question 3",
          prompt: "You see a Wi-Fi network with a familiar-looking name. What should you do?",
          options: [
            { id: "a", label: "Join it. A familiar name is usually the official one.", icon: "x" },
            { id: "b", label: "Use a trusted hotspot or VPN, or verify the official network.", correct: true, icon: "check" },
            { id: "c", label: "Ask a nearby stranger which network is best.", icon: "alert" },
          ],
          rationale: "A lookalike name is easy to copy. Prefer a network you already trust, and check the real name before you join.",
        },
        {
          id: "q_byod",
          title: "Question 4",
          prompt: "You want to use a personal tablet for work. What must be true?",
          options: [
            { id: "a", label: "You own the tablet, so it is fine.", icon: "x" },
            { id: "b", label: "Company rules allow it, and the required security controls are on.", correct: true, icon: "check" },
            { id: "c", label: "You can send the files through a personal messaging app instead.", icon: "alert" },
          ],
          rationale: "Owning the device is not approval. BYOD needs the company rule plus the controls that rule requires.",
        },
        {
          id: "q_ceo",
          title: "Question 5",
          prompt: "Your CEO urgently asks for a file through an unusual channel. What should you do?",
          options: [
            { id: "a", label: "Send it immediately. You can see that it is the CEO.", icon: "x" },
            { id: "b", label: "Verify through a separate channel you already trust.", correct: true, icon: "check" },
            { id: "c", label: "Approve the sign-in prompt first, then send the file.", icon: "alert" },
          ],
          rationale: "A face on a call and an urgent message are not proof. Confirm on a number or process you already had before you send anything.",
        },
      ],
    },
  ],

  edges: [
    { from: "b_intro", to: "b_cam_terminal" },
    { from: "b_cam_terminal", to: "b_hub" },
    { from: "b_hub", to: "b_quiz", on: "finish" },
  ],

  initialBeatId: "b_intro",
};
